import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Award, ChevronRight } from 'lucide-react';
import { useCurrency } from '@/context/CurrencyContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ArtisansPage = () => {
  const { slug } = useParams();
  const [artisans, setArtisans] = useState([]);
  const [selectedArtisan, setSelectedArtisan] = useState(null);
  const [artisanProducts, setArtisanProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { formatPrice } = useCurrency();

  useEffect(() => {
    fetchArtisans();
  }, []);

  useEffect(() => {
    if (slug) {
      fetchArtisan(slug);
    } else {
      setSelectedArtisan(null);
    }
  }, [slug]);

  const fetchArtisans = async () => {
    try {
      const response = await axios.get(`${API}/artisans`);
      setArtisans(response.data || []);
    } catch (error) {
      console.error('Error fetching artisans:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchArtisan = async (artisanSlug) => {
    try {
      const response = await axios.get(`${API}/artisans/${artisanSlug}`);
      setSelectedArtisan(response.data.artisan);
      setArtisanProducts(response.data.products || []);
    } catch (error) {
      console.error('Error fetching artisan:', error);
    }
  };

  // Single Artisan View
  if (selectedArtisan) {
    return (
      <div className="pt-32 pb-24" data-testid="artisan-detail-page">
        <div className="chaleur-container">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-8">
            <Link to="/artisans" className="hover:text-foreground">Artisans</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-foreground">{selectedArtisan.name}</span>
          </nav>

          <div className="grid md:grid-cols-2 gap-12 mb-24">
            <div className="aspect-square overflow-hidden">
              <img 
                src={selectedArtisan.image || 'https://images.unsplash.com/photo-1607746882042-944635dfe10e?w=800&q=80'}
                alt={selectedArtisan.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <p className="caption text-primary mb-4">{selectedArtisan.craft}</p>
              <h1 className="text-3xl md:text-4xl font-light mb-4">{selectedArtisan.name}</h1>
              <div className="flex items-center gap-4 text-sm text-muted-foreground mb-6">
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" /> {selectedArtisan.location}
                </span>
                <span className="flex items-center gap-1">
                  <Award className="w-4 h-4" /> {selectedArtisan.years_experience} years experience
                </span>
              </div>
              <p className="text-muted-foreground leading-relaxed mb-6">{selectedArtisan.bio}</p>
              <div className="prose text-muted-foreground">
                <p>{selectedArtisan.story}</p>
              </div>
            </div>
          </div>

          {/* Artisan's Products */}
          {artisanProducts.length > 0 && (
            <div>
              <h2 className="text-2xl font-light mb-8">Creations by {selectedArtisan.name}</h2>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                {artisanProducts.map(product => (
                  <Link key={product.id} to={`/product/${product.slug}`} className="product-card">
                    <div className="product-card-image">
                      <img 
                        src={product.images?.[0] || 'https://images.unsplash.com/photo-1585007326391-d4bfd01fb320?w=400&q=80'}
                        alt={product.name}
                      />
                    </div>
                    <div className="mt-4 space-y-1">
                      <h3 className="text-sm font-medium line-clamp-1">{product.name}</h3>
                      <span className="text-sm">{formatPrice(product.variants?.[0]?.price)}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Gallery */}
          {selectedArtisan.gallery?.length > 0 && (
            <div className="mt-24">
              <h2 className="text-2xl font-light mb-8">Behind the Scenes</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {selectedArtisan.gallery.map((img, idx) => (
                  <div key={idx} className="aspect-square overflow-hidden">
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // All Artisans Grid
  return (
    <div className="pt-32 pb-24" data-testid="artisans-page">
      {/* Hero */}
      <section className="chaleur-container mb-24">
        <div className="max-w-3xl">
          <p className="caption text-primary mb-4">The Guild</p>
          <h1 className="text-4xl md:text-5xl font-light mb-6">
            Meet the <span className="editorial-italic">Masters</span> Behind the Craft
          </h1>
          <p className="text-muted-foreground leading-relaxed">
            Every Chaleur piece is created by skilled artisans whose families have practiced 
            these crafts for generations. Their hands carry centuries of tradition, and their 
            stories are woven into every thread.
          </p>
        </div>
      </section>

      {/* Artisans Grid */}
      <section className="chaleur-container">
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)].map((_, idx) => (
              <div key={idx} className="animate-pulse">
                <div className="aspect-[4/5] bg-secondary mb-4" />
                <div className="h-6 bg-secondary rounded w-3/4 mb-2" />
                <div className="h-4 bg-secondary rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : artisans.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {artisans.map((artisan, idx) => (
              <Link 
                key={artisan.id}
                to={`/artisans/${artisan.slug}`}
                className="group animate-fade-in-up"
                style={{ animationDelay: `${idx * 0.1}s` }}
              >
                <div className="aspect-[4/5] overflow-hidden mb-4">
                  <img 
                    src={artisan.image || 'https://images.unsplash.com/photo-1607746882042-944635dfe10e?w=600&q=80'}
                    alt={artisan.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <h3 className="text-xl font-light mb-1 group-hover:text-primary transition-colors">
                  {artisan.name}
                </h3>
                <p className="text-sm text-muted-foreground">{artisan.craft} • {artisan.location}</p>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <p className="text-muted-foreground mb-8">
              Our artisan stories are coming soon. Each master craftsperson has a unique journey 
              that we're excited to share with you.
            </p>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                { name: 'Lakshmi Devi', craft: 'Handweaving', location: 'Gujarat' },
                { name: 'Ramesh Kumar', craft: 'Block Printing', location: 'Rajasthan' },
                { name: 'Meera Begum', craft: 'Embroidery', location: 'Kashmir' }
              ].map((artisan, idx) => (
                <div key={idx} className="text-center">
                  <div className="w-32 h-32 mx-auto rounded-full overflow-hidden mb-4 bg-secondary">
                    <img 
                      src={`https://images.unsplash.com/photo-${idx === 0 ? '1607746882042-944635dfe10e' : idx === 1 ? '1581803118522-7b72a50f7e9f' : '1573496359142-b8d87734a5a2'}?w=200&q=80`}
                      alt={artisan.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3 className="text-lg mb-1">{artisan.name}</h3>
                  <p className="text-sm text-muted-foreground">{artisan.craft} • {artisan.location}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default ArtisansPage;
